import { model, OnInit, signal } from "@angular/core";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { form } from "@angular/forms/signals";

import { Building, Check, Copy, Link, LucideAngularModule, Send, Users } from "lucide-angular";

import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { RolesStore } from "@documentation/state/roles.store";
import { NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";
import { User } from "@shared/models/user.model";
import { AIRolesHttpService } from "@shared/services/ai-roles-http.service";

import { BaseShareFormState, BaseShareModalComponent } from "./base-share-modal.component";

interface ShareFormState extends BaseShareFormState {}

@Component({
  selector: "component-share-ai-role-modal",
  imports: [ModalComponent, LucideAngularModule, ToggleComponent, NgbTypeaheadModule, FormsModule],
  templateUrl: "./share-ai-role-modal.component.html",
  styleUrl: "./share-ai-role-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShareAiRoleModalComponent extends BaseShareModalComponent<ShareFormState> implements OnInit {
  readonly roleId = model<string>("");

  private readonly _aiRolesHttpService = inject(AIRolesHttpService);
  private readonly _rolesStore = inject(RolesStore);

  // Icons
  protected readonly sendIcon = Send;
  protected readonly linkIcon = Link;
  protected readonly copyIcon = Copy;
  protected readonly checkIcon = Check;
  protected readonly buildingIcon = Building;
  protected readonly usersIcon = Users;

  // Signals
  protected readonly isOrgShared = signal<boolean>(false);

  // Form Signal
  protected readonly formState = signal<ShareFormState>({
    recipient: "",
    selectedUser: null
  });

  protected readonly form = form(this.formState);

  ngOnInit(): void {
    if (this.roleId()) {
      this.checkOrgShare();
    }
  }

  checkOrgShare(): void {
    this.loading.set(true);
    this._aiRolesHttpService.checkOrgShare(this.roleId()).subscribe({
      next: (res) => {
        this.isOrgShared.set(res.isShared);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  onUserSelected(event: NgbTypeaheadSelectItemEvent<User>): void {
    this.handleUserSelected(event, (u) => this.formState.update((s) => ({ ...s, selectedUser: u })));
  }

  updateRecipient(value: string): void {
    this.handleRecipientUpdate(value, this.formState(), (update) => this.formState.update((s) => ({ ...s, ...update })));
  }

  toggleShareWithOrganization(): void {
    const isTurningOn = !this.isOrgShared();
    // Optimistic update: Sync signal with UI state (which has likely already changed)
    this.isOrgShared.set(isTurningOn);

    if (!isTurningOn) {
      // Trying to Unshare
      this.runOrgShareFlow(
        false,
        {
          title: "Stop sharing with organization",
          message:
            "Are you sure you want to stop sharing this role with your organization? Users who already have a copy will keep it, but new users won't receive it automatically.",
          confirmText: "Stop Sharing"
        },
        () => {
          this._aiRolesHttpService.stopSharingWithOrganization(this.roleId()).subscribe({
            next: () => {
              this.isOrgShared.set(false);
              this._toast.success("Organization sharing stopped");
              this._rolesStore.notifyRoleUpdated(this.roleId());
            },
            error: () => {
              this._toast.error("Failed to update sharing");
              // Revert UI on error
              this.isOrgShared.set(true);
            }
          });
        },
        () => {
          // Cancelled: Revert the UI state with delay to force Angular change detection
          setTimeout(() => this.isOrgShared.set(true), 0);
        }
      );
    } else {
      // Trying to Share
      this.runOrgShareFlow(
        true,
        {
          title: "Share with organization",
          message: (count) =>
            `This will create a copy of this role for all ${count - 1} members of your organization and send them an email. Are you sure?`,
          confirmText: "Share"
        },
        () => {
          this.executeOrgShare();
        },
        () => {
          // Cancelled: Revert UI with delay
          setTimeout(() => this.isOrgShared.set(false), 0);
        }
      );
    }
  }

  private executeOrgShare(): void {
    this._aiRolesHttpService.shareWithOrganization(this.roleId()).subscribe({
      next: () => {
        this.isOrgShared.set(true);
        this._toast.success("Shared with organization");
        this._rolesStore.notifyRoleUpdated(this.roleId());
      },
      error: () => {
        this._toast.error("Failed to share");
        this.isOrgShared.set(false);
      }
    });
  }

  copyLink(): void {
    const url = window.location.origin + `/${DocumentationRoutesConstants.MAIN}/roles/import/${this.roleId()}`;
    this.copyLinkToClipboard(url);
  }

  shareRole(): void {
    if (!this.roleId()) return;
    const { recipient, selectedUser } = this.formState();
    const recipientValue = selectedUser ? selectedUser.username || selectedUser.email : recipient;

    if (!recipientValue) return;

    this.loading.set(true);

    this._aiRolesHttpService.shareWithUser(this.roleId(), recipientValue).subscribe({
      next: () => {
        this._toast.success("Role shared successfully");
        this.formState.update((s) => ({ ...s, recipient: "", selectedUser: null }));
        this.loading.set(false);
      },
      error: (err) => {
        this._toast.error(err.error?.message || "Failed to share role");
        this.loading.set(false);
      }
    });
  }
}
